/**
 * Purchasing feature module — IAP subscription/storefront/credit-purchase verbs,
 * the pending credit-purchase request state, and the outbound sender. Case
 * bodies, senders, and module state moved verbatim from embeddedBridge.ts.
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';
import { useAccountStore, isAccountSubscriptionTier, type AccountSubscriptionTier } from '@/store/accountStore.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import { useTelemetryStore, isTelemetryEntitlement } from '@/telemetry/telemetryStore';

// ─── Message types ───────────────────────────────────────────────────────

export interface SetSubscriptionStateMessage {
  type: 'setSubscriptionState';
  /** `tier` is OPTIONAL and stays optional: Unity omits it when the purchasing manager
   *  isn't up yet, and an older embedded bundle paired with a newer host must keep working
   *  off `isSubscribed` alone. Absent ⇒ leave the store's tier untouched. */
  payload: { isSubscribed: boolean; tier?: AccountSubscriptionTier; entitlement?: string };
  // `entitlement` (20261007) is the TELEMETRY copy of the ladder and is also optional: Unity
  // omits it until the App Store receipt has loaded (TimelineWebViewPanel.GetTelemetryEntitlement),
  // whereas `tier` reads "free" in that window. Telemetry must never take "free" from `tier`,
  // because "free" would then also mean "receipts not loaded yet". Absent ⇒ leave the stored
  // entitlement untouched.
}

export interface SetStorefrontRegionMessage {
  type: 'setStorefrontRegion';
  payload: { countryCode: string; isUS: boolean };
}

export interface CreditPurchaseResultMessage {
  type: 'creditPurchaseResult';
  payload: { success: boolean };
}

export type PurchasingMessage =
  | SetSubscriptionStateMessage
  | SetStorefrontRegionMessage
  | CreditPurchaseResultMessage;

// ─── Credit purchase state ───────────────────────────────────────────────

let pendingCreditPurchase: {
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
} | null = null;

/** Pack metadata stashed before the IAP call so we can fire pack_purchased on success. */
let _pendingPackDetails: { pack_id: string; credits: number; price_cents: number } | null = null;

/** Call this at the purchase initiation site (before requestCreditPurchase) so the
 *  creditPurchaseResult bridge handler can fire the pack_purchased telemetry event. */
export function setPendingPackDetails(details: { pack_id: string; credits: number; price_cents: number }): void {
  _pendingPackDetails = details;
}

// ─── Inbound handlers ────────────────────────────────────────────────────

export const purchasingHandlers = {
  setSubscriptionState: (msg: SetSubscriptionStateMessage) => {
    useAccountStore.getState().setSubscribed(msg.payload.isSubscribed);
    // Runtime-guarded, not just typed: the tier vocabulary is Unity's and can grow, and a newer
    // host paired with this bundle must not put a label-less string in the store (the footer's
    // tier→key map would render t(undefined)). Unknown ⇒ leave the stored tier alone, exactly
    // like the absent case — the Projects-route receiver chain already drops unknowns the same way.
    if (isAccountSubscriptionTier(msg.payload.tier)) {
      useAccountStore.getState().setSubscriptionTier(msg.payload.tier);
    }
    // Same vocabulary, same ladder, but from the GUARDED field (see the message type above):
    // keeps telemetry's entitlement current so a trial start or a purchase mid-session is
    // reflected on every later event, without ever stamping "free" before the receipt loaded.
    if (isTelemetryEntitlement(msg.payload.entitlement)) {
      useTelemetryStore.getState().setEntitlement(msg.payload.entitlement);
    }
  },
  setStorefrontRegion: (msg: SetStorefrontRegionMessage) => {
    useAccountStore.getState().setStorefrontRegion(msg.payload.countryCode, msg.payload.isUS);
  },
  creditPurchaseResult: (msg: CreditPurchaseResultMessage) => {
    if (pendingCreditPurchase) {
      clearTimeout(pendingCreditPurchase.timeout);
      if (msg.payload.success) {
        if (_pendingPackDetails) {
          trackEvent('pack_purchased', _pendingPackDetails);
        }
        pendingCreditPurchase.resolve();
      } else {
        pendingCreditPurchase.reject(new Error('Credit purchase failed'));
      }
      pendingCreditPurchase = null;
    }
    _pendingPackDetails = null;
    // Refresh balance regardless — even on failure the state may have changed
    useAccountStore.getState().refreshBalance();
  },
} satisfies HandlerMap;

// ─── Credit purchase requests ─────────────────────────────────────────────

/**
 * Ask Unity to initiate an Apple IAP credit pack purchase.
 * Returns a promise that resolves when the purchase completes
 * or rejects on error/cancel or 3-minute timeout.
 */
export function requestCreditPurchase(packId: string): Promise<void> {
  if (pendingCreditPurchase) {
    return Promise.reject(new Error('Credit purchase already in progress'));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      if (pendingCreditPurchase) {
        pendingCreditPurchase = null;
        reject(new Error('Credit purchase timed out'));
      }
    }, 3 * 60 * 1000);

    pendingCreditPurchase = { resolve, reject, timeout };
    enqueueSendToNative('purchaseCreditPack', { packId });
  });
}
